import React from 'react';
import { flexRender } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';

import { TableHead } from 'shadcn/components/ui/Table';
import { cn } from 'shadcn/lib/utils';

export const PO_ITEM_SORTABLE_COLUMN_IDS = new Set(['itemMasterSku', 'externalSku', 'name']);

/**
 * Normalizes a cell value for text sorting. Empty/null values sort last.
 * @param {unknown} value
 * @returns {string|null}
 */
function normalizeSortValue(value) {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized || null;
}

/**
 * Case-insensitive alphanumeric sort for PO item text columns.
 * @param {import('@tanstack/react-table').Row<unknown>} rowA
 * @param {import('@tanstack/react-table').Row<unknown>} rowB
 * @param {string} columnId
 * @returns {number}
 */
export function purchaseOrderItemTextSortingFn(rowA, rowB, columnId) {
    const a = normalizeSortValue(rowA.getValue(columnId));
    const b = normalizeSortValue(rowB.getValue(columnId));

    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Applies sortable configuration to a TanStack column definition.
 * @param {import('@tanstack/react-table').ColumnDef<unknown>} column
 * @returns {import('@tanstack/react-table').ColumnDef<unknown>}
 */
export function applyPurchaseOrderItemSorting(column) {
    const columnId = column.id ?? column.accessorKey;
    const isSortable = PO_ITEM_SORTABLE_COLUMN_IDS.has(columnId);

    return {
        ...column,
        enableSorting: isSortable,
        ...(isSortable ? { sortingFn: purchaseOrderItemTextSortingFn } : {}),
    };
}

/**
 * Renders a sortable table header cell for PO item tables.
 * @param {Object} props
 * @param {import('@tanstack/react-table').Header<unknown, unknown>} props.header
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
export function PurchaseOrderItemSortableTableHead({ className, header }) {
    const canSort = header.column.getCanSort();
    const sorted = header.column.getIsSorted();

    return (
        <TableHead
            key={header.id}
            className={cn(
                'whitespace-nowrap text-left text-xs font-semibold uppercase tracking-wide',
                canSort && 'cursor-pointer select-none',
                className,
            )}
            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
        >
            {header.isPlaceholder ? null : (
                <div className="flex items-center gap-1">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {canSort &&
                        (() => {
                            if (sorted === 'asc') return <ArrowUp className="h-4 w-4 shrink-0" />;
                            if (sorted === 'desc') return <ArrowDown className="h-4 w-4 shrink-0" />;
                            return <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />;
                        })()}
                </div>
            )}
        </TableHead>
    );
}
